import { useTranslation } from "react-i18next";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { ProviderPatientPayload, usePostProviderGroupPatient } from "./usePostProviderGroupPatient";
import { Box } from "@material-ui/core";
import {
  CTextField,
  CSelect,
  HFlex,
  ProgressButton,
  Text,
  DialogSuccessNotice,
  useErrorContext,
  FileUpload,
  CAutocomplete,
  ErrorBox,
} from "shared";
import { useMountState } from "utils/hooks";
import { delay } from "utils/misc";
import { BODY_PARTS, TIMEZONES } from "./options";
import { FORM_NOTIFICATION_DELAY } from "theme";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Send } from "assets";
import { useUsProviderGroupClinicOptions } from "../utils/useUsProviderGroupClinicOptions";
import { useReferralSources } from "shared/ValueTable";

interface InviteProviderPatientFormProps {
  onClose: () => void;
}

export const InviteProviderPatientForm: React.FC<InviteProviderPatientFormProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const getMountState = useMountState();
  const { options: usProviderGroupClinicOptions } = useUsProviderGroupClinicOptions();

  const referralSources = useReferralSources();
  const postProviderGroupPatient = usePostProviderGroupPatient();

  const form = useForm<ProviderPatientPayload>({
    mode: "onChange",
  });
  const {
    formState: { errors },
  } = form;

  const watchedProviderGroup = form.watch("us_provider_group_clinic_id");
  const isMotionMSK =
    usProviderGroupClinicOptions.find(option => option.value === watchedProviderGroup)?.label === "Motion MSK";

  const onSubmit = form.handleSubmit(async formData => {
    try {
      await postProviderGroupPatient.mutateAsync(formData);
      if (!getMountState()) return;
      await delay(FORM_NOTIFICATION_DELAY);
      if (!getMountState()) return;
      onClose();
    } catch (error) {
      setError(t`errors.could_not_send_invite`, error);
    }
  });

  if (postProviderGroupPatient.isSuccess) {
    const recipient =
      // @ts-ignore
      postProviderGroupPatient?.data?.email || postProviderGroupPatient?.data?.phone_number;
    const body = recipient
      ? `${t("form.invitation_successfully_sent_to")} ${recipient}`
      : t("patients.invite_sent");
    return <DialogSuccessNotice heading={t("patients.invite_sent")} body={body} onClose={onClose} />;
  }

  return (
    <Box mx={3}>
      <Text heading bold id="invite-provider-patient">
        {t("form.invite_provider_patient")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <CSelect
            name="us_provider_group_clinic_id"
            label={t("form.provider")}
            items={usProviderGroupClinicOptions}
            margin="normal"
            size="small"
            required
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <HFlex gap={2}>
            <CTextField
              fullWidth
              name="patient_first_name"
              label={t("form.first_name")}
              margin="normal"
              size="small"
              error={!!errors.patient_first_name}
              required
            />
            <CTextField
              fullWidth
              name="patient_last_name"
              label={t("form.last_name")}
              margin="normal"
              size="small"
              error={!!errors.patient_last_name}
              required
            />
          </HFlex>

          <CTextField name="email" label={t("form.email")} type="email" margin="normal" size="small" fullWidth />

          {/* TODO: Fix adding +1 after cleaing the phone_number */}
          <Controller
            name="phone_number"
            control={form.control}
            render={() => (
              <PhoneInput
                disableCountryGuess
                disableDropdown
                countryCodeEditable={true}
                country={"us"}
                onlyCountries={["us"]}
                inputProps={{
                  name: "phone_number",
                }}
                onChange={inputValue => {
                  form.setValue("phone_number", inputValue);
                }}
                placeholder={t("form.phone_number")}
                containerStyle={{ width: "100%", margin: "16px 0 8px" }}
                inputStyle={{ width: "100%" }}
              />
            )}
          />

          <CTextField name="phone_country_code" type="hidden" variant="standard" noClear defaultValue={"US"} required />

          {isMotionMSK && (
            <CTextField
              fullWidth
              name="external_partner_user_id"
              label={t("form.external_partner_user_id")}
              margin="normal"
              size="small"
              error={!!errors.external_partner_user_id}
              required
            />
          )}

          <CTextField name="language_code" type="hidden" variant="standard" noClear defaultValue={"en"} required />

          <HFlex gap={2}>
            <CSelect
              name="alt_language"
              label={t("form.language")}
              items={[
                { value: "en", label: "English" },
                { value: "es", label: "Spanish" },
              ]}
              margin="normal"
              size="small"
              defaultValue={"en"}
              fullWidth
            >
              {({ label }) => <Text>{label}</Text>}
            </CSelect>

            <CSelect
              name="invite_timezone"
              label={t("form.home_timezone")}
              items={TIMEZONES}
              margin="normal"
              size="small"
              fullWidth
            >
              {({ label }) => <Text>{label}</Text>}
            </CSelect>
          </HFlex>

          <CSelect
            name="invited_joint"
            label={t("form.invited_joint")}
            items={BODY_PARTS}
            margin="normal"
            size="small"
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          {!isMotionMSK && (
            <>
              <HFlex gap={2}>
                <CTextField
                  fullWidth
                  name="physician_first_name"
                  label={t("form.physician_first_name")}
                  margin="normal"
                  size="small"
                  required
                />
                <CTextField
                  fullWidth
                  name="physician_last_name"
                  label={t("form.physician_last_name")}
                  margin="normal"
                  size="small"
                  required
                />
              </HFlex>

              <CTextField
                name="invite_source"
                type="hidden"
                variant="standard"
                noClear
                defaultValue={"chenmed_invite"}
                required
              />

              <FileUpload
                size="small"
                name="file"
                label={t("patients.document")}
                fullWidth
                style={{ margin: "16px 0 8px" }}
              />

              <CAutocomplete
                size="small"
                name="source"
                getOptionLabel={({ value }) => value ?? "NAME MISSING"}
                loading={referralSources.isLoading}
                noOptionsText={referralSources.isError && <ErrorBox error={referralSources.error} />}
                label={t`form.source`}
                options={referralSources.data ?? []}
              />
            </>
          )}

          <HFlex justifyContent="center" my={6}>
            <ProgressButton loading={postProviderGroupPatient.isLoading} endIcon={<Send width={20} />}>
              {t("buttons.send_invitation")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};
